/**
 * PDFChartRenderer - Renders Nivo charts to SVG strings for PDF embedding
 * Uses non-responsive Nivo components with explicit dimensions to avoid
 * ResizeObserver issues in hidden containers.
 */

import { createRoot } from 'react-dom/client';
import React from 'react';
import { Line } from '@nivo/line';
import { Bar } from '@nivo/bar';
import { TreeMap } from '@nivo/treemap';
import { AreaBump } from '@nivo/bump';
import { Funnel } from '@nivo/funnel';
import { extractSVGFromElement } from '@/lib/pdf/svg-utils';

// Fixed dimensions for chart rendering (matching PDF content width)
const CHART_RENDER_WIDTH = 515;
const CHART_RENDER_HEIGHT = 250;
const PDF_MAX_HEIGHT = 250;
const PDF_MARGIN = { top: 10, right: 130, bottom: 80, left: 60 };

const chartTheme = {
  text: { fontFamily: 'Montserrat, sans-serif', fontWeight: 400 as const },
  axis: { ticks: { text: { fontFamily: 'Montserrat, sans-serif', fontSize: 11 } }, legend: { text: { fontFamily: 'Montserrat, sans-serif', fontSize: 12 } } },
  legends: { text: { fontFamily: 'Montserrat, sans-serif', fontSize: 10, dominantBaseline: 'middle' as const, dy: 4 } },
  labels: { text: { fontFamily: 'Montserrat, sans-serif', fontSize: 11 } },
};

/**
 * PDFChart - Renders a Nivo chart using non-responsive components
 * with explicit width/height for reliable hidden-container rendering.
 */
const PDFChart: React.FC<{ config: Record<string, unknown>; width: number; height: number }> = ({
  config,
  width,
  height,
}) => {
  const margin = PDF_MARGIN;
  const type = config.type as string;

  if (type === 'line') {
    return (
      <Line
        data={config.data as Array<{ id: string; data: Array<{ x: string | number; y: number }> }>}
        width={width}
        height={height}
        theme={chartTheme}
        margin={{ ...margin, right: 20, bottom: 60 }}
        xScale={(config.xScale as { type: 'point' }) || { type: 'point' }}
        yScale={
          (config.yScale as { type: 'linear'; min: 'auto'; max: 'auto' }) || {
            type: 'linear',
            min: 'auto',
            max: 'auto',
          }
        }
        curve={(config.curve as 'monotoneX') || 'monotoneX'}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 15,
          tickRotation: 0,
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 10,
          tickRotation: 0,
          legend: (config.axisLeftLabel as string) || undefined,
          legendOffset: -50,
          legendPosition: 'middle',
        }}
        colors={(config.colors as string[]) || ['#290BD9', '#D1D5DB', '#059669']}
        lineWidth={(config.lineWidth as number) || 2}
        pointSize={(config.pointSize as number) || 5}
        pointColor={{ theme: 'background' }}
        pointBorderWidth={2}
        pointBorderColor={{ from: 'serieColor' }}
        useMesh={false}
        enableGridX={config.enableGridX !== false}
        enableGridY={config.enableGridY !== false}
        animate={false}
        isInteractive={false}
        legends={[
          {
            anchor: 'bottom',
            direction: 'row',
            translateX: 0,
            translateY: 50,
            itemsSpacing: 20,
            itemWidth: 120,
            itemHeight: 20,
            symbolSize: 10,
            symbolSpacing: 6,
            symbolShape: 'circle',
          }
        ]}
      />
    );
  }

  if (type === 'bar') {
    const layout = (config.layout as 'vertical' | 'horizontal') || 'vertical';

    // Custom label layer: positions labels above bars when they're too short
    const SmartLabelLayer = (props: { bars: readonly { x: number; y: number; width: number; height: number; data: { value: number | null }; color: string }[] }) => (
      <g>
        {props.bars.map((bar, i) => {
          const isSmall = bar.height < 28;
          return (
            <text
              key={i}
              x={bar.x + bar.width / 2}
              y={isSmall ? bar.y - 6 : bar.y + bar.height / 2}
              textAnchor="middle"
              dominantBaseline={isSmall ? 'auto' : 'central'}
              fontFamily="Montserrat, sans-serif"
              fontSize={11}
              fontWeight={600}
              fill={isSmall ? '#374151' : '#ffffff'}
            >
              {bar.data.value}
            </text>
          );
        })}
      </g>
    );

    return (
      <Bar
        data={config.data as Array<Record<string, string | number>>}
        keys={config.keys as string[]}
        indexBy={config.indexBy as string}
        groupMode={(config.groupMode as 'grouped' | 'stacked') || 'stacked'}
        width={width}
        height={height}
        theme={chartTheme}
        margin={{ ...margin, right: 20, bottom: 60 }}
        padding={0.3}
        valueScale={{ type: 'linear' }}
        indexScale={{ type: 'band', round: true }}
        colors={(config.colors as string[]) || ['#290BD9', '#D1D5DB', '#059669']}
        borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        axisTop={null}
        axisRight={null}
        axisBottom={
          layout === 'horizontal'
            ? null
            : {
                tickSize: 5,
                tickPadding: 15,
                tickRotation: 0,
              }
        }
        axisLeft={{
          tickSize: 5,
          tickPadding: 10,
          tickRotation: 0,
          legend: (config.axisLeftLabel as string) || undefined,
          legendOffset: -50,
          legendPosition: 'middle',
        }}
        enableGridX={config.enableGridX !== false}
        enableGridY={config.enableGridY !== false}
        enableLabel={false}
        layers={['grid', 'axes', 'bars', SmartLabelLayer, 'markers', 'legends']}
        animate={false}
        isInteractive={false}
        legends={[
          {
            dataFrom: 'keys',
            anchor: 'bottom',
            direction: 'row',
            translateX: 0,
            translateY: 50,
            itemsSpacing: 20,
            itemWidth: 110,
            itemHeight: 20,
            symbolSize: 10,
            symbolSpacing: 6,
          }
        ]}
      />
    );
  }

  if (type === 'treemap') {
    return (
      <TreeMap
        data={config.data as { name: string; children?: Array<{ name: string; value?: number }> }}
        width={width}
        height={height}
        theme={chartTheme}
        margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
        identity="name"
        value="value"
        tile={
          (config.tile as string) === 'resquarify'
            ? 'squarify'
            : (config.tile as 'squarify' | 'binary' | 'dice' | 'slice' | 'sliceDice') || 'squarify'
        }
        leavesOnly={config.leavesOnly !== false}
        innerPadding={(config.innerPadding as number) || 3}
        outerPadding={(config.outerPadding as number) || 3}
        colors={(config.colors as { scheme: 'nivo' }) || { scheme: 'nivo' as const }}
        borderWidth={2}
        borderColor={{ from: 'color', modifiers: [['darker', 0.1]] }}
        enableLabel={config.enableLabel !== false}
        label={(node: { value?: number }) => `${node.value?.toLocaleString() || 0}`}
        labelSkipSize={0}
        labelTextColor={{ from: 'color', modifiers: [['darker', 1.2]] }}
        orientLabel={false}
        animate={false}
        isInteractive={false}
      />
    );
  }

  if (type === 'areabump') {
    return (
      <AreaBump
        data={config.data as Array<{ id: string; data: Array<{ x: string; y: number }> }>}
        width={width}
        height={height}
        theme={chartTheme}
        margin={margin}
        spacing={8}
        colors={(config.colors as string[]) || ['#8884d8', '#82ca9d', '#ff7300']}
        blendMode="multiply"
        fillOpacity={(config.fillOpacity as number) || 0.6}
        borderWidth={0}
        borderColor={{ theme: 'background' }}
        startLabel={false}
        endLabel={false}
        axisTop={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
        }}
        animate={false}
        isInteractive={false}
      />
    );
  }

  if (type === 'funnel') {
    return (
      <Funnel
        data={config.data as Array<{ id: string; value: number; label?: string }>}
        width={width}
        height={height}
        theme={chartTheme}
        margin={margin}
        direction={(config.direction as 'horizontal') || 'horizontal'}
        spacing={(config.spacing as number) || 0}
        shapeBlending={(config.shapeBlending as number) || 0.66}
        colors={(config.colors as string[]) || ['#ef4444', '#f97316', '#eab308']}
        borderWidth={1}
        borderColor={{ from: 'color', modifiers: [['darker', 0.3]] }}
        enableLabel={config.enableLabel !== false}
        labelColor={(config.labelColor as string) || '#ffffff'}
        beforeSeparatorLength={40}
        beforeSeparatorOffset={20}
        afterSeparatorLength={40}
        afterSeparatorOffset={20}
        currentPartSizeExtension={10}
        currentBorderWidth={40}
        animate={false}
        isInteractive={false}
      />
    );
  }

  return null;
};

/**
 * Renders a Nivo chart to an SVG string using non-responsive components.
 * @param config - Chart configuration (same format as Graph component)
 * @returns Promise resolving to SVG string
 */
export async function renderChartToSVG(config: Record<string, unknown>): Promise<string> {
  return new Promise((resolve, reject) => {
    const chartHeight = Math.min(Number(config.height) || CHART_RENDER_HEIGHT, PDF_MAX_HEIGHT);
    const container = document.createElement('div');
    container.style.cssText = `
      position: fixed;
      left: 0;
      top: 0;
      width: ${CHART_RENDER_WIDTH}px;
      height: ${chartHeight}px;
      opacity: 0;
      pointer-events: none;
      z-index: -1;
    `;
    document.body.appendChild(container);

    const root = createRoot(container);
    root.render(<PDFChart config={config} width={CHART_RENDER_WIDTH} height={chartHeight} />);

    // Wait for chart to render, then extract SVG
    const extractSVG = () => {
      const svgString = extractSVGFromElement(container);

      // Cleanup
      root.unmount();
      document.body.removeChild(container);

      if (svgString) {
        resolve(svgString);
      } else {
        reject(new Error('Failed to extract SVG from chart'));
      }
    };

    // Use requestAnimationFrame to wait for render, with timeout fallback
    let attempts = 0;
    const maxAttempts = 20;

    const waitForRender = () => {
      attempts++;
      const svg = container.querySelector('svg');

      if (svg && svg.querySelector('path, rect, circle, line, text')) {
        // Chart has rendered with actual content
        extractSVG();
      } else if (attempts < maxAttempts) {
        requestAnimationFrame(waitForRender);
      } else {
        // Timeout - try to extract anyway
        extractSVG();
      }
    };

    requestAnimationFrame(waitForRender);
  });
}

/**
 * Renders multiple charts to SVG strings
 * @param configs - Array of chart configurations with indices
 * @param onProgress - Optional callback for progress updates
 * @returns Promise resolving to Map of index -> SVG string
 */
export async function renderChartsToSVG(
  configs: Array<{ index: number; config: Record<string, unknown> }>,
  onProgress?: (completed: number, total: number) => void
): Promise<Map<number, string>> {
  const results = new Map<number, string>();

  for (let i = 0; i < configs.length; i++) {
    const { index, config } = configs[i];

    try {
      const svgString = await renderChartToSVG(config);
      results.set(index, svgString);
    } catch (error) {
      console.error(`Failed to render chart ${index}:`, error);
      // Continue with other charts even if one fails
    }

    // Report progress
    if (onProgress) {
      onProgress(i + 1, configs.length);
    }

    // Yield to main thread between charts
    await new Promise((resolve) => setTimeout(resolve, 0));
  }

  return results;
}
